import { Color3 } from '@babylonjs/core/Maths/math';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { CSG } from '@babylonjs/core/Meshes/csg';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { eJoinType } from '@models/classes/enums';
import { JoinGeometryFeature } from '@models/classes/features/join-geometry-feature';
import { CustomMeshNode } from '@models/classes/meshes/custom-mesh-node';
import { MeshNode } from '@models/classes/meshes/mesh-node';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { createJoinGeometryVisualization } from '@view/helpers/join-geometry-helper';
import { Subscription } from 'rxjs';
import { FeatureRenderer } from './feature';
import { ensureNormalsExist } from './utils';

const joinedMeshes = new Map<string, Mesh>();

export class JoinGeometryRenderer extends FeatureRenderer<JoinGeometryFeature> {
    modelToken = Token.JoinGeometryFeature;
    bbSubscriptions = new Map<string, Subscription>();
    targetMeshVisualizationMap = new Map<string, Mesh>();
    parentMeshVisualizationMap = new Map<string, Mesh>();
    previewLayer: UtilityLayerRenderer = this.viewer.utilityLayer;

    updateMeshGeometry(
        feature: JoinGeometryFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);

        const targetMesh = joinedMeshes.get(feature._dynamicId);
        const thisMesh = this.viewer.getRendererNode(feature._parent!);

        if (
            targetMesh instanceof Mesh &&
            thisMesh instanceof Mesh &&
            targetMesh.geometry &&
            targetMesh.geometry.getTotalIndices() > 0 &&
            targetMesh != thisMesh
        ) {
            const workingVertexData = vertexData.shallowClone();
            const tempMesh = new Mesh('csg-temp');
            tempMesh.doNotSerialize = true;
            SubmeshVertexData.CopyToRef(newVertexData, workingVertexData);
            if (!workingVertexData.positions || !workingVertexData.indices) {
                throw 'Invalid geometry';
            }
            const targetTransform = thisMesh.computeWorldMatrix(true);
            workingVertexData.applyToMesh(tempMesh);

            let previewMesh = this.parentMeshVisualizationMap.get(feature.id);

            if (!previewMesh) {
                const newMeshInstance = new Mesh('preview-mesh');
                newMeshInstance.doNotSerialize = true;
                workingVertexData.applyToMesh(newMeshInstance);
                this.parentMeshVisualizationMap.set(feature.id, newMeshInstance);
                if (newMeshInstance instanceof Mesh) {
                    previewMesh = createJoinGeometryVisualization(
                        newMeshInstance,
                        this.previewLayer.utilityLayerScene,
                        Color3.Yellow()
                    );
                    this.parentMeshVisualizationMap.set(feature.id, previewMesh);
                    previewMesh.setEnabled(false);
                    if (feature._parent) {
                        const featureParentMesh = this.viewer.getRendererNode(feature._parent);
                        newMeshInstance.position.addInPlace(featureParentMesh.absolutePosition);
                    }
                }
            }

            ensureNormalsExist(tempMesh);

            const resultCsg = CSG.FromMesh(tempMesh);

            const targetMeshMatchTransform = targetMesh.clone();
            const targetMeshTransform = targetMeshMatchTransform.computeWorldMatrix(true);
            targetMeshMatchTransform.parent = null;
            targetTransform.invert();
            targetMeshTransform.multiplyToRef(targetTransform, targetMeshTransform);
            targetMeshMatchTransform.rotationQuaternion = new Quaternion();
            targetMeshTransform.decompose(
                targetMeshMatchTransform.scaling,
                targetMeshMatchTransform.rotationQuaternion,
                targetMeshMatchTransform.position
            );

            try {
                const opCsg = CSG.FromMesh(targetMeshMatchTransform);
                if (feature.joinType === eJoinType.cut) {
                    resultCsg.subtractInPlace(opCsg);
                } else if (feature.joinType === eJoinType.intersect) {
                    resultCsg.intersectInPlace(opCsg);
                } else if (feature.joinType === eJoinType.union) {
                    resultCsg.unionInPlace(opCsg);
                }
            } catch (e) {
                console.warn('Could not merge geometry for join ' + feature.name, e);
                return newVertexData;
            }

            if (resultCsg) {
                const resultMeshCsg = resultCsg.toMesh('temp2', null, undefined, true);

                const resultVertexData = SubmeshVertexData.ExtractFromMesh(resultMeshCsg);
                newVertexData.positions = resultVertexData.positions;
                if (newVertexData.normals) {
                    newVertexData.normals = resultVertexData.normals;
                }
                if (newVertexData.uvs) {
                    newVertexData.uvs = resultVertexData.uvs;
                }
                newVertexData.indices = resultVertexData.indices;

                // Sometimes the CSG will add another material to the multiMaterial to represent the cut face. Change that material to be the base material, otherwise it will be black.
                const existingMaterials = new Set<number>();
                // get a list of all the materials that are already in the multiMaterial
                if (newVertexData.submeshData) {
                    newVertexData.submeshData.forEach(submeshData => {
                        existingMaterials.add(submeshData.materialIndex);
                    });
                }
                if (resultVertexData.submeshData) {
                    resultVertexData.submeshData.forEach(submeshData => {
                        if (!existingMaterials.has(submeshData.materialIndex)) {
                            submeshData.materialIndex = 0;
                        }
                    });
                }

                newVertexData.submeshData = resultVertexData.submeshData;
                resultMeshCsg.dispose();
            }
            tempMesh.dispose();
            targetMeshMatchTransform.dispose();
        }
        return newVertexData;
    }

    updatePreviewMesh(node: JoinGeometryFeature) {
        let featureInstance = this.targetMeshVisualizationMap.get(node.id);
        const scene = this.previewLayer.utilityLayerScene;
        const targetMesh = joinedMeshes.get(node._dynamicId);
        if (targetMesh && !featureInstance) {
            featureInstance = createJoinGeometryVisualization(targetMesh.clone(), scene, Color3.Yellow());
            featureInstance.isPickable = false;
            featureInstance.setEnabled(false);
            this.targetMeshVisualizationMap.set(node.id, featureInstance);
            return featureInstance;
        } else if (targetMesh) {
            this.deleteTargetMeshVisualization(node as ITrackedClass<JoinGeometryFeature>);
            featureInstance = createJoinGeometryVisualization(targetMesh.clone(), scene, Color3.Yellow());
            featureInstance.isPickable = false;
            featureInstance.setEnabled(false);
            this.targetMeshVisualizationMap.set(node.id, featureInstance);
            return featureInstance;
        }
    }

    update3dInternal(feature: ITrackedClass<JoinGeometryFeature>) {
        if (feature.changes.hasAny('meshId', 'enabled')) {
            const existingSubscription = this.bbSubscriptions.get(feature._dynamicId);
            if (existingSubscription) {
                existingSubscription.unsubscribe();
            }

            if (feature.enabled) {
                const targetMeshNode = feature._manager?.getById<SpaceNode>(feature.meshId);
                if (targetMeshNode && targetMeshNode instanceof MeshNode) {
                    // if this is a custom mesh node and the geometry isn't loaded, we have to load it
                    if (
                        targetMeshNode instanceof CustomMeshNode &&
                        targetMeshNode.hasGeometry &&
                        !targetMeshNode.geometryLoaded
                    ) {
                        this.viewer.rendererManager.mesh.loadNewGeometry(targetMeshNode);
                    }
                    const targetMesh = this.viewer.getRendererNode(targetMeshNode);

                    if (targetMesh instanceof Mesh && feature._parent) {
                        joinedMeshes.set(feature._dynamicId, targetMesh);

                        this.bbSubscriptions.set(
                            feature._dynamicId,
                            // TODO This should also watch the parent group for positional changes, otherwise the join won't update when the feature's parent mesh moves.
                            // But watch out for infinite loops
                            this.viewer.bbCache.watchForChanges(targetMeshNode).subscribe(() => {
                                feature.setPropertyIsModified('meshId', true);
                            })
                        );
                    } else {
                        console.warn('Could not create CSG for join geometry');
                    }
                }
            }
        }

        let featureInstance = this.targetMeshVisualizationMap.get(feature.id);
        const originalMesh = this.parentMeshVisualizationMap.get(feature.id);

        if (feature._previewVisible) {
            featureInstance = this.updatePreviewMesh(feature);
        }

        if (feature._previewVisible && feature.enabled) {
            if (feature.joinType === eJoinType.cut) {
                originalMesh?.setEnabled(false);
                featureInstance?.setEnabled(true);
            } else if (feature.joinType === eJoinType.intersect) {
                originalMesh?.setEnabled(true);
                featureInstance?.setEnabled(true);
            } else {
                originalMesh?.setEnabled(false);
                featureInstance?.setEnabled(false);
            }
        } else {
            originalMesh?.setEnabled(false);
            featureInstance?.setEnabled(false);
        }
    }

    delete3d(feature: ITrackedClass<JoinGeometryFeature>, preserveCache: boolean) {
        if (!preserveCache) {
            joinedMeshes.delete(feature._dynamicId);
        }
        this.bbSubscriptions.get(feature._dynamicId)?.unsubscribe();
        this.bbSubscriptions.delete(feature._dynamicId);
        this.deleteTargetMeshVisualization(feature);

        const targetMeshCopy = this.parentMeshVisualizationMap.get(feature.id);
        targetMeshCopy?.dispose(false, true);
        this.parentMeshVisualizationMap.delete(feature.id);
        super.delete3d(feature);
    }

    deleteTargetMeshVisualization(feature: ITrackedClass<JoinGeometryFeature>) {
        const previewMesh = this.targetMeshVisualizationMap.get(feature.id);
        previewMesh?.dispose(false, true);
        this.targetMeshVisualizationMap.delete(feature.id);
    }

    dispose() {
        this.bbSubscriptions.forEach(s => {
            s.unsubscribe();
        });
        this.bbSubscriptions.clear();
        joinedMeshes.clear();
        super.dispose();
    }
}
